/**
 * CUTOVER — the old business, loaded in (audit §3, 0056/0057).
 *
 * Paste a CSV from the old register, DRY-RUN it (always first — the commit
 * button only wakes after a dry run of the same text), read the findings,
 * then commit. A second commit of the same file creates nothing, and the
 * screen says so rather than pretending work happened. Below: the loader's
 * worklist and the opening balances still waiting for a signature.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun } from '../hooks.js';
import { Banner, Button, Card, Loading, Money, Pill, Register, Select, Text, TextArea } from '../ui.js';
import { Page } from '../shell.js';
import { date, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('cutover')!.messages;

type Kind = 'challans' | 'receipts' | 'toll_fill';
const KINDS: { value: Kind; label: string; command: 'cutover.importChallans' | 'cutover.importReceipts' | 'cutover.importTollFill'; columns: string }[] = [
  { value: 'challans', label: 'Challans (DC/RC)', command: 'cutover.importChallans', columns: 'legacy_number, direction, event_date, customer, site, serials, vehicle, note' },
  { value: 'receipts', label: 'Payment receipts (archival)', command: 'cutover.importReceipts', columns: 'original_number, customer, amount, paid_on, method, reference' },
  { value: 'toll_fill', label: 'Toll-fill records (staged)', command: 'cutover.importTollFill', columns: 'source_ref, customer, serial, direction, event_date, gas, quantity, unit, note' },
];

interface RunAnswer { runId: number; dryRun: boolean; rows: number; imported: number; skippedDuplicates: number; findings: { row: number; finding: string }[] }

export function Cutover({ session }: { session: Session }) {
  const runsAsked = useAsk(() => api.view('cutover.runs', {}), [], MESSAGES);
  const worklistAsked = useAsk(() => api.view('cutover.worklist', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);

  const [kind, setKind] = useState<Kind>('challans');
  const [source, setSource] = useState('');
  const [csv, setCsv] = useState('');
  const [answer, setAnswer] = useState<RunAnswer | null>(null);
  const [rehearsedText, setRehearsedText] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<number | null>(null);
  const [settling, setSettling] = useState<Record<number, string>>({});

  const chosen = KINDS.find((k) => k.value === kind)!;
  const mayImport = can(session, 'cutover.import_challans') || can(session, 'cutover.import_receipts') || can(session, 'cutover.import_toll_fill');

  async function run(dry: boolean) {
    const ok = await runner.run(async (retryKey) => {
      const body = { source: source.trim() || 'pasted CSV', csv, ...(dry ? {} : { dryRun: false }) };
      const opts = retryKey ? { idempotencyKey: retryKey } : {};
      // spelled out one by one — each command is its own door with its own permission (D179)
      const r = kind === 'challans'
        ? await api.command('cutover.importChallans', body, opts)
        : kind === 'receipts'
          ? await api.command('cutover.importReceipts', body, opts)
          : await api.command('cutover.importTollFill', body, opts);
      const v = r.value as RunAnswer;
      setAnswer(v);
      if (dry) setRehearsedText(csv);
      return v;
    });
    if (ok) { runsAsked.reload(); if (!dry) worklistAsked.reload(); }
  }

  async function accept(b: { runId: number; customerId: number; primarySiteId: number | null }) {
    if (b.primarySiteId === null) return;
    const ok = await runner.run((retryKey) => api.command('cutover.acceptOpeningBalance',
      { runId: b.runId, customerId: b.customerId, siteId: b.primarySiteId! },
      retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) worklistAsked.reload();
  }

  async function settle(itemId: number) {
    const settlement = (settling[itemId] ?? '').trim();
    if (!settlement) return;
    const ok = await runner.run(() => api.command('cutover.settle', { itemId, settlement }));
    if (ok) worklistAsked.reload();
  }

  const findingsAsked = useAsk(
    () => (openRun === null ? Promise.resolve(null) : api.view('cutover.findings', { runId: openRun })),
    [openRun], MESSAGES);

  // only the FIRST load blanks the screen (the billing screen's lesson):
  // a reload after a commit must not unmount the panels mid-answer
  if (runsAsked.loading && !runsAsked.data) return <Page title="Cutover"><Loading what="the cutover runs" /></Page>;
  if (runsAsked.error) return <Page title="Cutover"><Banner kind="error" title={runsAsked.error.title} {...(runsAsked.error.detail ? { detail: runsAsked.error.detail } : {})} /></Page>;
  const runs = runsAsked.data!.rows;
  const wl = worklistAsked.data;

  return (
    <Page title="Cutover" sub="Loading the old register in, once, when this company moves onto CTS. A company already trading here does not need this screen.">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}

      {mayImport ? (
        <Card title="Import from the old register" sub={`columns for ${chosen.label}: ${chosen.columns}`}>
          <Select name="import-kind" label="What the file is" value={kind} onChange={(v) => { setKind(v as Kind); setAnswer(null); setRehearsedText(null); }}
                  options={KINDS.map((k) => ({ value: k.value, label: k.label }))} />
          <Text name="import-source" label="Where it came from" value={source} onChange={setSource} help="the export's file name, so the run can be traced" />
          <TextArea name="import-csv" label="The CSV" value={csv} onChange={(v) => { setCsv(v); setAnswer(null); }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={() => { void run(true); }} disabled={runner.busy || !csv.trim()}>{runner.busy ? 'Working…' : 'Dry run'}</Button>
            <span title={rehearsedText !== csv ? 'dry-run this exact text first' : ''}>
              <Button kind="primary" onClick={() => { void run(false); }}
                      disabled={runner.busy || !csv.trim() || rehearsedText !== csv}>
                Commit
              </Button>
            </span>
          </div>
          {answer ? (
            <div style={{ marginTop: 12 }}>
              <Banner kind={answer.findings.length ? 'warn' : 'good'}
                      title={`${answer.dryRun ? 'Dry run' : 'Commit'} · run #${answer.runId}: ${answer.rows} rows — ${answer.imported} imported, ${answer.skippedDuplicates} already in, ${answer.findings.length} findings.`}
                      {...(answer.dryRun ? { detail: 'Nothing was created. Commit becomes available for this exact text.' } : {})} />
              {answer.findings.length ? (
                <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
                  {answer.findings.map((f) => <li key={f.row}><strong>row {f.row}</strong> — {f.finding}</li>)}
                </ul>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : <Banner kind="info" title="You can read the cutover, not run it." detail="Importing needs the cutover.import permissions." />}

      <OpeningPosition session={session} onDone={() => { runsAsked.reload(); worklistAsked.reload(); }} />

      <Card title="Runs" sub="every import, checked or loaded — the record of what was tried">
        <Register
          columns={[
            { key: 'run', head: 'Run', cell: (r: { runId: number }) => `#${r.runId}` },
            { key: 'kind', head: 'Kind', cell: (r: { kind: string }) => words(r.kind) },
            { key: 'source', head: 'Source', cell: (r: { sourceName: string; sourceHash: string }) => <span title={r.sourceHash}>{r.sourceName}</span> },
            { key: 'mode', head: 'Mode', cell: (r: { dryRun: boolean }) => <Pill tone={r.dryRun ? 'muted' : 'good'}>{r.dryRun ? 'dry run' : 'committed'}</Pill> },
            { key: 'imported', head: 'Imported', right: true, cell: (r: { imported: number }) => String(r.imported) },
            { key: 'skipped', head: 'Already in', right: true, cell: (r: { skipped: number }) => String(r.skipped) },
            { key: 'findings', head: 'Findings', right: true, cell: (r: { findings: number }) => String(r.findings) },
            { key: 'when', head: 'When', cell: (r: { createdAt: string }) => date(r.createdAt) },
          ]}
          rows={runs} empty="Nothing has been imported yet."
          onOpen={(r) => setOpenRun(r.runId)} />
        {openRun !== null ? (
          <div style={{ marginTop: 12 }}>
            <h3 style={{ margin: '0 0 6px' }}>Run #{openRun} — the rows</h3>
            {findingsAsked.loading ? <Loading what="the run's rows" /> : findingsAsked.data ? (
              <Register
                columns={[
                  { key: 'row', head: 'Row', right: true, cell: (r: { rowNo: number }) => String(r.rowNo) },
                  { key: 'outcome', head: 'Outcome', cell: (r: { outcome: string }) => <Pill tone={r.outcome === 'imported' ? 'good' : r.outcome === 'finding' ? 'danger' : 'muted'}>{words(r.outcome)}</Pill> },
                  { key: 'finding', head: 'Finding', cell: (r: { finding: string | null }) => r.finding ?? '—' },
                ]}
                rows={findingsAsked.data.rows} empty="The run recorded no rows (a clean dry run records only findings)." />
            ) : null}
          </div>
        ) : null}
      </Card>

      <Card title="The worklist" sub="what the loader could not decide — handed to a person, never guessed">
        {worklistAsked.loading ? <Loading what="the worklist" /> : wl ? (<>
          <Register
            columns={[
              { key: 'kind', head: 'Kind', cell: (i: { kind: string }) => words(i.kind) },
              { key: 'ref', head: 'Reference', cell: (i: { reference: string }) => i.reference },
              { key: 'detail', head: 'What is wrong', cell: (i: { detail: string }) => i.detail },
              { key: 'state', head: 'State', cell: (i: { settledAt: string | null; settlement: string | null; itemId: number }) => i.settledAt
                  ? <Pill tone="good">{`settled — ${i.settlement ?? ''}`}</Pill>
                  : can(session, 'cutover.settle')
                    ? <span style={{ display: 'flex', gap: 6 }}>
                        <Text name={`settle-${i.itemId}`} label="" value={settling[i.itemId] ?? ''} onChange={(v) => setSettling((old) => ({ ...old, [i.itemId]: v }))} />
                        <Button small onClick={() => { void settle(i.itemId); }} disabled={runner.busy || !(settling[i.itemId] ?? '').trim()}>Settle</Button>
                      </span>
                    : <Pill>open</Pill> },
            ]}
            rows={wl.items} empty="Nothing waits on a person." />
          {wl.openingBalances.length ? (
            <div style={{ marginTop: 14 }}>
              <h3 style={{ margin: '0 0 6px' }}>Opening balances</h3>
              <Register
                columns={[
                  { key: 'cust', head: 'Customer', cell: (b: { customerName: string }) => b.customerName },
                  { key: 'amount', head: 'Amount', right: true, cell: (b: { amountPaise: number }) => <Money paise={b.amountPaise} plain /> },
                  { key: 'asof', head: 'As of', cell: (b: { asOf: string }) => date(b.asOf) },
                  { key: 'state', head: 'State', cell: (b: { signedOffAt: string | null; runId: number; customerId: number; primarySiteId: number | null }) => b.signedOffAt
                      ? <Pill tone="good">signed and posted</Pill>
                      : can(session, 'cutover.accept_opening_balance')
                        ? <span title={b.primarySiteId === null ? 'the customer has no primary site' : ''}>
                            <Button small kind="primary" onClick={() => { void accept(b); }} disabled={runner.busy || b.primarySiteId === null}>Accept and post</Button>
                          </span>
                        : <Pill tone="danger">awaiting signature</Pill> },
                ]}
                rows={wl.openingBalances} empty="No opening balances staged." />
              <p className="muted" style={{ marginTop: 8 }}>Only a signed figure reaches the ledger — accepting posts the opening balance as a bill on the customer's primary site.</p>
            </div>
          ) : null}
        </>) : null}
      </Card>
    </Page>
  );
}


/**
 * THE OPENING POSITION (cutover.load — the last of the fifteen absent): the
 * open trips and the opening balances, pasted, DRY-RUN first, then loaded.
 * Balances only STAGE here; money moves when a signed figure is accepted
 * below (D61). Simple comma rows — these two files carry no free text.
 */
function OpeningPosition({ session, onDone }: { session: Session; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [asOf, setAsOf] = useState(today());
  const [source, setSource] = useState('');
  const [tripsCsv, setTripsCsv] = useState('');
  const [balancesCsv, setBalancesCsv] = useState('');
  const [answer, setAnswer] = useState<{ runId: number; dryRun: boolean; tripsImported: number; balancesStaged: number; worklist: { kind: string; reference: string; detail: string }[] } | null>(null);
  const [rehearsed, setRehearsed] = useState<string | null>(null);

  function parsedTrips() {
    return tripsCsv.split('\n').map((l) => l.trim()).filter((l) => l.length > 0).map((l) => {
      const [cylinderRef, customerRef, outDate, billedUpTo] = l.split(',').map((x) => x.trim());
      return { cylinderRef: cylinderRef ?? '', customerRef: customerRef ?? '', outDate: outDate ?? '', ...(billedUpTo ? { billedUpTo } : {}) };
    });
  }
  function parsedBalances() {
    return balancesCsv.split('\n').map((l) => l.trim()).filter((l) => l.length > 0).map((l) => {
      const [customerRef, rupees] = l.split(',').map((x) => x.trim());
      return { customerRef: customerRef ?? '', amountPaise: Math.round(Number(rupees ?? '0') * 100) };
    });
  }

  async function run(dry: boolean) {
    const ok = await runner.run(async (retryKey) => {
      const r = await api.command('cutover.load', {
        source: source.trim() || 'pasted opening position', asOf,
        openTrips: parsedTrips(), openingBalances: parsedBalances(),
        ...(dry ? {} : { dryRun: false }),
      }, retryKey ? { idempotencyKey: retryKey } : {});
      setAnswer(r.value as typeof answer);
      if (dry) setRehearsed(tripsCsv + '\u0000' + balancesCsv);
      return r;
    });
    if (ok && !dry) onDone();
  }

  const mayLoad = can(session, 'cutover.load');
  const text = tripsCsv + '\u0000' + balancesCsv;
  return (
    <Card title="The opening position" sub="open trips (cylinder, customer, out date, billed-up-to) and opening balances (customer, rupees) — dry run first; a balance moves money only when a person signs it">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {!mayLoad ? <p className="muted">Loading the opening position needs the cutover.load function.</p> : (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Text name="load-as-of" label="As of" type="date" value={asOf} onChange={setAsOf} />
            <Text name="load-source" label="Where it came from" value={source} onChange={setSource} />
          </div>
          <TextArea name="load-trips" label="Open trips CSV" value={tripsCsv} onChange={(v) => { setTripsCsv(v); setAnswer(null); }} />
          <TextArea name="load-balances" label="Opening balances CSV" value={balancesCsv} onChange={(v) => { setBalancesCsv(v); setAnswer(null); }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={() => { void run(true); }} disabled={runner.busy || (!tripsCsv.trim() && !balancesCsv.trim())}>Dry-run the position</Button>
            <span title={rehearsed !== text ? 'dry-run this exact text first' : ''}>
              <Button kind="primary" onClick={() => { void run(false); }} disabled={runner.busy || rehearsed !== text}>Load it</Button>
            </span>
          </div>
          {answer ? (
            <div style={{ marginTop: 12 }}>
              <Banner kind={answer.worklist.length ? 'warn' : 'good'}
                      title={`${answer.dryRun ? 'Dry run' : 'Loaded'} · run #${answer.runId}: ${answer.tripsImported} trips, ${answer.balancesStaged} balances staged, ${answer.worklist.length} for the worklist.`}
                      {...(answer.dryRun ? { detail: 'Nothing was written. Load becomes available for this exact text.' } : {})} />
              {answer.worklist.length ? (
                <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
                  {answer.worklist.map((w, i) => <li key={i}><strong>{w.reference}</strong> — {w.detail}</li>)}
                </ul>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
